/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Framework from '@greenville/framework';
import {
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    ListItem,
    List,
    IconButton,
    Collapse,
    Divider,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandIcon from './icons/ExpandIcon';
import CollapseIcon from './icons/CollapseIcon';
import mixins from '../assets/styles/mixins.scss';

const LaunchCodeTitle = styled(Typography)({
    '&&': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'TT Commons',
        fontSize: 30,
        lineHeight: '110%',
        color: '#000000',
        paddingBottom: 10,
        paddingTop: 41,
        borderBottom: '1px solid #E8EEFA'
    }
});

const LaunchList = styled(List)({
    '&&': {
        padding: 0
    }
});

const LaunchCodeDesc = styled(Typography)({
    '&&': {
        fontFamily: 'TT Commons',
        fontSize: 16,
        lineHeight: '110%',
        color: '#4F5561',
        paddingBottom: 14,
        fontWeight: 600,
        display: 'inline-block'
    }
});

const LaunchCodeRightDesc = styled(Typography)({
    '&&': {
        fontFamily: 'Hind',
        fontSize: 14,
        fontStyle: 'normal',
        letterSpacing: 0,
        display: 'inline-block',
        float: 'right;',
        paddingTop: 6,
        width: 165,
        height: 21,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
    }
});

const LaunchCodeDivider = styled(Divider)({
    '&&': {
        marginTop: 6,
        marginBottom: 0,
        background: '#E8EEFA',
        border: 'none',
        borderStyle: 'none',
        height: 1
    }
});

const LaunchCodeListDesc = styled(Typography)({
    '&&': {
        display: 'inline-block',
        fontFamily: 'TT Commons',
        fontSize: 18,
        color: '#4F5561',
        paddingBottom: 14,
        paddingTop: 14,
        fontWeight: 600
    }
});

const LaunchCodeListRightDesc = styled(Typography)({
    '&&': {
        fontFamily: 'Hind',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        letterSpacing: 0,
        display: 'inline-block',
        float: 'right;',
        paddingTop: 15
    }
});

const CollapseListRightDesc = styled(Typography)({
    '&&': {
        fontFamily: 'Hind',
        fontSize: 14,
        fontStyle: 'normal',
        letterSpacing: 0,
        float: 'right',
        paddingTop: 4,
        width: '17%'
    }
});

const CloseButton = styled(IconButton)({
    '&&': {
        width: 14,
        height: 14,
        padding: 0,
        position: 'absolute',
        top: 26,
        right: 24,
        '& .MuiSvgIcon-root': {
            width: 18,
            height: 18
        }
    }
});

const LaunchCodeDialogContent = styled(DialogContent)({
    '&&': {
        padding: '8px 24px'
    }
});

const ContentList = styled(List)({
    '&&': {
        background: 'white',
        paddingTop: 0,
        paddingBottom: 0
    }
});

const MenuItemTypography = styled(Typography)({
    '&&': {
        color: 'black',
        fontSize: 14,
        height: 12,
        marginLeft: -27
    }
});

const ListItemStyled = styled(ListItem)({
    '&&': {
        border: '2px solid rgba(0, 0, 0, 0)',
        borderBottom: `1px solid ${mixins.lightGreyBgColor}`,
        paddingBottom: 26,
        paddingTop: 15,
        height: 45
    }
});

const IconButtonStyled = styled(IconButton)({
    '&&': {
        '&.MuiIconButton-root': {
            color: '#05112A'
        },
        fontSize: 9,
        paddingTop: 11
    }
});

const CollapseListItem = styled(ListItem)({
    '&&': {
        background: '#F7F9FD',
        border: '1px solid #E8EEFA',
        height: 45
    }
});

const CreateBy = styled(Typography)({
    '&&': {
        display: 'inline-block',
        fontSize: 14,
        paddingLeft: 38
    }
});

const CreateByDiv = styled('div')({
    '&&': {
        width: '100%'
    }
});

const LaunchCode = ({ open, email, onClose, history }) => {
    const language = Framework.getStoreRegistry().getStore('language');
    const logTimes = JSON.parse(JSON.stringify(history));
    const logLength = logTimes.length;
    const [selectedIndex, setSelectedIndex] = useState('');

    const handleMenuClick = (e, indexValue) => {
        let selectedInd = ''
        logTimes.map((log, index) => {
            if (indexValue === index) {
                selectedInd = index
            }
        })
        if (selectedIndex !== selectedInd) {
            setSelectedIndex(selectedInd);
        } else {
            setSelectedIndex('');
        }
    }

    const getTime = (localtime) => {
        const time = new Date(localtime).toLocaleTimeString('en', {
            timeStyle: 'short',
            hour12: true,
            timeZone: 'EST'
        });
        return `${time} EST`
    }

    return (
        <div>
            <Dialog
                open={open}
                aria-labelledby="launchcode-modal-title"
                aria-describedby="launchcode-modal-description"
                PaperProps={{
                    style: {
                        height: 611,
                        width: 429,
                        background: '#F7F9FD',
                        paddingBottom: 38
                    },
                }}
                className="dialog-zindex"
            >
                <DialogTitle id="launchcode-modal-title">
                    <CloseButton aria-label="close" onClick={onClose}>
                        <CloseIcon />
                    </CloseButton>
                    <LaunchCodeTitle variant="h6" component="h2">
                        {
                            <FormattedMessage {...language.getText('launchcode.LAUNCHDIALOGTITLE')} />
                        }
                    </LaunchCodeTitle>
                </DialogTitle>
                <LaunchCodeDialogContent id="launchcode-modal-description">
                    <div >
                        <LaunchCodeDesc>
                            {
                                <FormattedMessage {...language.getText('launchcode.ACCOUNTEMAIL')} />
                            }
                        </LaunchCodeDesc>
                        <LaunchCodeRightDesc>
                            {email}
                        </LaunchCodeRightDesc>
                    </div>
                    <LaunchCodeDivider />
                    <div>
                        <LaunchCodeListDesc>
                            {
                                <FormattedMessage {...language.getText('launchcode.CODEINLAST')} />
                            }
                        </LaunchCodeListDesc>
                        <LaunchCodeListRightDesc>
                            {logLength}
                        </LaunchCodeListRightDesc>
                    </div>
                    <ContentList
                        id="log-list"
                        role="menu"
                    >
                        {
                            logTimes.map((logTime, index) => (
                                <>
                                    <ListItemStyled
                                        id={logTime.deviceId}
                                        key={logTime.deviceId}
                                        button
                                        onClick={(e) => {handleMenuClick(e, index)}}
                                    >
                                        <ListItemIcon >
                                            <IconButtonStyled >
                                                {selectedIndex === index ? <CollapseIcon /> : <ExpandIcon />}
                                            </IconButtonStyled>
                                        </ListItemIcon>
                                        <ListItemText >
                                            <MenuItemTypography>
                                                {
                                                    <FormattedMessage {...language.getText('launchcode.TIMESENT')} />
                                                }
                                                {getTime(logTime.serverUpdatedDateTime)}
                                            </MenuItemTypography>
                                        </ListItemText>
                                    </ListItemStyled>
                                    <Collapse
                                        in={selectedIndex === index}
                                        timeout='auto'
                                        unmountOnExit
                                    >
                                        <LaunchList
                                            component='li'
                                        >
                                            <CollapseListItem
                                                id={logTime.deviceId}
                                                key={logTime.deviceId}
                                            >
                                                <CreateByDiv>
                                                    <CreateBy>
                                                        {
                                                            <FormattedMessage {...language.getText('launchcode.CREATEBY')} />
                                                        }
                                                    </CreateBy>
                                                    <CollapseListRightDesc>
                                                        {logTime.isImpersonated ? 'CS Team' : 'User'}
                                                    </CollapseListRightDesc>
                                                </CreateByDiv>
                                            </CollapseListItem>
                                            <CollapseListItem
                                                id={logTime.deviceId}
                                                key={logTime.deviceId}
                                            >
                                                <CreateByDiv>
                                                    <CreateBy>
                                                        {
                                                            <FormattedMessage {...language.getText('launchcode.sentvia')} />
                                                        }
                                                    </CreateBy>
                                                    <CollapseListRightDesc>
                                                        {logTime.deliveryChannel}
                                                    </CollapseListRightDesc>
                                                </CreateByDiv>
                                            </CollapseListItem>
                                        </LaunchList>
                                    </Collapse>
                                </>
                            ))
                        }
                    </ContentList>
                </LaunchCodeDialogContent>
            </Dialog>
        </div>
    );
}

export default LaunchCode;
